import { msg } from "@lingui/core/macro";
import { createEffect, createMemo, createSignal, Show, Suspense } from "solid-js";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { createPowerSyncSingle } from "@listto/shared/solid";
import { useLocation, useNavigate } from "@tanstack/solid-router";
import { useTodoFocus } from "../hooks/use-todo-focus";
import {
  addToMyDayMutation,
  cn,
  deleteTodoMutation,
  getListDetailQuery,
  getTodoDetailQuery,
  type ListWithMeta,
  queryKeys,
  toggleTodoMutation,
  updateTodoMutation,
} from "@listto/shared";
import type { Todo } from "@listto/powersync";
import { Checkbox } from "@/components/ui/checkbox";
import { DetailActionButton } from "@/components/ui/detail-action-button";
import { ChevronRight, Info, ListTree, Star, Sun, Trash2 } from "lucide-solid";
import { GlassSurface } from "@/components/ui/glass-surface";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { useI18n } from "@/providers/i18n-provider";
import { SubtaskList } from "./subtask-list";
import { AttachmentsList } from "./attachments-list";
import { ListThemeProvider } from "../../../providers/list-theme-provider";
import { TodoDetailSkeleton } from "./todo-detail-skeleton";
import { DEBOUNCE_DELAY_MS, OFFLINE_CACHE_STALE_TIME_MS } from "@/lib/constants";
import { getListIconStyle } from "./todo-detail.helpers";
import { ScheduleField } from "./schedule/schedule-field";
import type { ScheduleValue } from "./schedule/schedule-panel";
import { useTodoDetailKeyboard } from "../hooks/use-todo-detail-keyboard";

interface TodoDetailProps {
  todoId: string;
  onDelete?: () => void;
  /**
   * When false, the panel is mounted but inactive (used by the detail carousel
   * to preload adjacent todos without stealing focus or capturing shortcuts).
   * Defaults to true.
   */
  isActive?: boolean;
  isEntranceCompleted?: boolean;
}

export function TodoDetail(props: TodoDetailProps) {
  const { t } = useI18n();
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();

  const [scheduleOpen, setScheduleOpen] = createSignal(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = createSignal(false);

  const todo = createPowerSyncSingle<Todo>({
    queryKey: () => queryKeys.todos.detail(props.todoId),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      return dbInstance ? getTodoDetailQuery(dbInstance, props.todoId) : null;
    },
    enabled: () => Boolean(props.todoId) && Boolean(drizzle()),
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
    placeholderData: (prev) => prev,
    suspense: false,
  });

  // Keep a stable reference to the last loaded todo data to prevent UI flickering
  // when the query is invalidated/refetched (e.g. during Silent Sync transition or mutations)
  let lastValidTodo: Todo | null = null;
  const stableTodo = createMemo(() => {
    const current = todo.data;
    if (current) {
      lastValidTodo = current;
      return current;
    }
    return lastValidTodo;
  });

  const location = useLocation();
  const navigate = useNavigate();
  const { lastFocusedTodoInput, setLastFocusedTodoInput } = useTodoFocus();
  let titleRef: HTMLTextAreaElement | undefined = undefined;
  let completedBtnRef: HTMLButtonElement | undefined = undefined;
  let notesRef: HTMLTextAreaElement | undefined = undefined;
  const [containerRef, setContainerRef] = createSignal<HTMLDivElement>();

  const data = () => {
    const val = stableTodo();
    if (val === null) {
      throw new Error("Todo data accessed before it was loaded");
    }
    return val;
  };

  const scheduleValue = (): ScheduleValue => ({
    due_date: data().due_date ?? null,
    reminder_at: data().reminder_at ?? null,
    recurrence_rule: data().recurrence_rule ?? null,
  });

  const handleUpdate = async (updates: Partial<Parameters<typeof updateTodoMutation>[2]>) => {
    const dbInstance = drizzle();
    if (!dbInstance) {
      return;
    }
    try {
      await updateTodoMutation(dbInstance, props.todoId, updates);
    } catch (err) {
      console.error("Error updating todo:", err);
    }
  };

  let textUpdateTimeout: ReturnType<typeof setTimeout> | undefined = undefined;
  const debouncedHandleUpdate = (updates: Partial<Parameters<typeof updateTodoMutation>[2]>) => {
    clearTimeout(textUpdateTimeout);
    textUpdateTimeout = setTimeout(() => {
      void handleUpdate(updates);
    }, DEBOUNCE_DELAY_MS);
  };

  const handleToggleMyDay = async (currentMyDay: string | null) => {
    const dbInstance = drizzle();
    if (!dbInstance) {
      return;
    }
    try {
      await addToMyDayMutation(dbInstance, props.todoId, !currentMyDay);
    } catch (err) {
      console.error("Error toggling my day:", err);
    }
  };

  const handleToggleTodo = async () => {
    const dbInstance = drizzle();
    if (!dbInstance) {
      return;
    }
    try {
      await toggleTodoMutation(dbInstance, props.todoId, data().completed === 1, data());
    } catch (err) {
      console.error("Error toggling todo completion:", err);
    }
  };

  createEffect(() => {
    const dataLoaded = stableTodo();
    const active = props.isActive !== false;
    if (dataLoaded) {
      // Wait a frame for DOM elements to fully paint
      requestAnimationFrame(() => {
        if (titleRef) {
          titleRef.style.height = "auto";
          titleRef.style.height = `${titleRef.scrollHeight}px`;
        }
        if (notesRef) {
          notesRef.style.height = "auto";
          notesRef.style.height = `${notesRef.scrollHeight}px`;
        }
        if (!active) {
          return;
        }
        if (lastFocusedTodoInput() === "title" && titleRef) {
          titleRef.focus({ preventScroll: true });
          const len = titleRef.value.length;
          titleRef.setSelectionRange(len, len);
        } else if (completedBtnRef) {
          completedBtnRef.focus({ preventScroll: true });
        }
      });
    }
  });
  const listDetail = createPowerSyncSingle<ListWithMeta>({
    queryKey: () => queryKeys.lists.detail(stableTodo()?.list_id ?? undefined),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      const listId = stableTodo()?.list_id;
      return dbInstance && listId ? getListDetailQuery(dbInstance, listId) : null;
    },
    enabled: () => Boolean(stableTodo()?.list_id) && Boolean(drizzle()),
    placeholderData: (prev) => prev,
    suspense: false,
  });

  const isAlreadyInList = () => {
    const listId = stableTodo()?.list_id;
    if (!listId) {
      return true;
    }
    return location().pathname.includes(`/lists/${listId}`);
  };

  const listToShow = () => {
    const list = listDetail.data;
    if (stableTodo()?.list_id && !isAlreadyInList() && list) {
      return list;
    }
    return undefined;
  };

  useTodoDetailKeyboard({
    isActive: () => props.isActive !== false,
    containerRef,
    getTodo: data,
    onOpenSchedule: () => setScheduleOpen(true),
    onUpdate: (updates) => void handleUpdate(updates),
    onToggleMyDay: (currentMyDay) => void handleToggleMyDay(currentMyDay),
    onToggleTodo: () => void handleToggleTodo(),
    onRequestDelete: () => setIsDeleteDialogOpen(true),
    onGoToList: () => {
      const list = listToShow();
      if (list) {
        void navigate({
          to: "/lists/$listId",
          params: { listId: list.id },
        });
      }
    },
  });

  return (
    <div
      ref={setContainerRef}
      class="flex flex-col min-h-full"
      data-active-todo-detail={props.isActive === false ? "false" : "true"}
    >
      <Show
        when={props.isEntranceCompleted !== false && stableTodo()}
        fallback={<TodoDetailSkeleton />}
      >
        <ListThemeProvider
          listId={data().list_id}
          important={data().important === 1}
          myDayDate={data().my_day_date}
        >
          <div class="flex flex-col flex-1 animate-todo-detail-entrance will-change-[transform,opacity]">
            {/* Header: Title & Completed */}
            <div class="flex items-center gap-3 pl-3.5">
              <button
                ref={(el) => {
                  completedBtnRef = el;
                }}
                type="button"
                class="flex items-center justify-center h-8 w-8 rounded-lg transition-all duration-200 active:scale-90 hover:bg-accent/50 cursor-pointer shrink-0 focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none focus:outline-none"
                onClick={() => void handleToggleTodo()}
              >
                <Checkbox
                  checked={data().completed === 1}
                  onChange={() => {
                    /* empty */
                  }}
                  class="h-5 w-5 pointer-events-none"
                  tabIndex={-1}
                />
              </button>
              <textarea
                value={data().text ?? ""}
                rows={1}
                onInput={(e) => {
                  // Fallback for browsers without field-sizing support
                  e.currentTarget.style.height = "auto";
                  e.currentTarget.style.height = `${e.currentTarget.scrollHeight}px`;
                  debouncedHandleUpdate({ text: e.currentTarget.value });
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    e.currentTarget.blur();
                  }
                }}
                onFocus={() => setLastFocusedTodoInput("title")}
                onBlur={() => {
                  if (lastFocusedTodoInput() === "title") {
                    setLastFocusedTodoInput(null);
                  }
                }}
                ref={(el) => {
                  titleRef = el;
                  // Initial adjustment for fallback
                  requestAnimationFrame(() => {
                    el.style.height = "auto";
                    el.style.height = `${el.scrollHeight}px`;
                  });
                }}
                class={cn(
                  "todo-title-input flex-1 border-none outline-none focus:outline-none bg-transparent p-0 text-xl font-bold focus-visible:ring-0 transition-all duration-200 resize-none overflow-hidden [field-sizing:content]",
                  data().completed === 1
                    ? "line-through text-muted-foreground/50 italic"
                    : "text-foreground",
                )}
              />
            </div>

            <Show when={listToShow()}>
              {(list) => (
                <GlassSurface class="flex flex-col p-1.5 mt-4">
                  <button
                    type="button"
                    onClick={() => {
                      void navigate({
                        to: "/lists/$listId",
                        params: { listId: list().id },
                      });
                    }}
                    class="group/listbtn w-full flex items-center gap-3 h-11 px-3 rounded-lg transition-all duration-200 cursor-pointer text-foreground/80 hover:bg-muted/10 relative overflow-hidden active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  >
                    <div class="absolute inset-0 bg-foreground/5 opacity-0 group-hover/listbtn:opacity-100 group-active/listbtn:opacity-100 group-active/listbtn:bg-foreground/10 transition-all duration-200 pointer-events-none" />
                    <div
                      class="h-6 w-6 rounded-md flex items-center justify-center text-sm shrink-0 leading-none"
                      style={getListIconStyle(list().color)}
                    >
                      {list().icon ?? "📋"}
                    </div>
                    <span class="flex-1 text-sm font-medium text-left truncate">{list().name}</span>
                    <span class="text-[10px] font-semibold text-muted-foreground/60 shrink-0">
                      {t(msg`Go to list`)}
                    </span>
                    <kbd
                      class="h-4.5 min-w-[15px] inline-flex items-center justify-center rounded border border-primary/20 bg-primary/5 px-1.5 font-mono text-[9px] font-bold text-primary transition-all duration-200 opacity-0 group-hover/listbtn:opacity-100 group-focus-within/listbtn:opacity-100 group-focus/listbtn:opacity-100 select-none cursor-help shrink-0"
                      title={t(msg`Go to list: Press G to open the list containing this task`)}
                    >
                      {t(msg`G`)}
                    </kbd>
                    <ChevronRight class="h-3.5 w-3.5 text-muted-foreground/40 shrink-0 transition-transform duration-200 group-hover/listbtn:translate-x-0.5" />
                  </button>
                </GlassSurface>
              )}
            </Show>

            {/* Quick Actions */}
            <GlassSurface class="flex flex-col p-1.5 gap-0.5 mt-4">
              <DetailActionButton
                label={
                  data().important === 1 ? t(msg`Marked as important`) : t(msg`Mark as important`)
                }
                icon={Star}
                isActive={data().important === 1}
                onClick={() => void handleUpdate({ important: data().important === 1 ? 0 : 1 })}
                shortcut={t(msg`I`)}
                shortcutTitle={t(
                  "Toggle task importance: Press I to mark or unmark this task as important",
                )}
              />

              <DetailActionButton
                label={data().my_day_date ? t(msg`Added to My Day`) : t(msg`Add to My Day`)}
                icon={Sun}
                isActive={Boolean(data().my_day_date)}
                onClick={() => void handleToggleMyDay(data().my_day_date)}
                shortcut={t(msg`D`)}
                shortcutTitle={t(
                  msg`Toggle My Day: Press D to add or remove this task from My Day`,
                )}
              />

              <ScheduleField
                value={scheduleValue}
                onChange={(patch) => void handleUpdate(patch)}
                open={scheduleOpen}
                setOpen={setScheduleOpen}
                shortcut={t(msg`T`)}
                shortcutTitle={t(
                  "Add date & reminder: Press T to set a due date, reminder, or recurrence rule",
                )}
              />
            </GlassSurface>

            {/* Subtasks */}
            <GlassSurface class="flex flex-col p-1.5 mt-2 gap-3 group/steps">
              <div class="flex items-center justify-between px-3 py-3 text-sm font-semibold text-muted-foreground">
                <div class="flex items-center gap-2">
                  <div class="flex justify-center w-5 shrink-0">
                    <ListTree class="h-4.5 w-4.5" />
                  </div>
                  {t(msg`Steps`)}
                </div>
                <kbd
                  class="h-4.5 min-w-[15px] inline-flex items-center justify-center rounded border border-primary/20 bg-primary/5 px-1.5 font-mono text-[9px] font-bold text-primary opacity-0 group-hover/steps:opacity-100 transition-opacity select-none cursor-help"
                  title={t(msg`Add step: Press Y to add a new step`)}
                >
                  {t(msg`Y`)}
                </kbd>
              </div>
              <div class="px-3 pb-1.5">
                <Suspense
                  fallback={
                    <div class="space-y-3 animate-pulse">
                      <div class="flex items-center gap-2">
                        <div class="h-4.5 w-4.5 bg-muted/40 rounded" />
                        <div class="h-5 bg-muted/40 rounded flex-1" />
                      </div>
                      <div class="flex items-center gap-2">
                        <div class="h-4.5 w-4.5 bg-muted/40 rounded" />
                        <div class="h-5 bg-muted/40 rounded flex-1" />
                      </div>
                    </div>
                  }
                >
                  <SubtaskList todoId={props.todoId} />
                </Suspense>
              </div>
            </GlassSurface>

            <GlassSurface class="flex flex-col gap-1 p-1.5 mt-2">
              <div class="px-3 py-2">
                <Suspense
                  fallback={
                    <div class="flex items-center gap-3 animate-pulse py-1">
                      <div class="flex h-10 w-10 shrink-0 items-center justify-center rounded bg-muted/40" />
                      <div class="flex-1 space-y-2">
                        <div class="h-3 bg-muted/40 rounded w-1/2" />
                        <div class="h-2 bg-muted/40 rounded w-1/4" />
                      </div>
                    </div>
                  }
                >
                  <AttachmentsList todoId={props.todoId} />
                </Suspense>
              </div>
            </GlassSurface>

            {/* Note */}
            <GlassSurface class="flex flex-col p-1.5 mt-2 gap-3 group/notes">
              <div class="flex items-center justify-between px-3 py-3 text-sm font-semibold text-muted-foreground">
                <div class="flex items-center gap-2">
                  <div class="flex justify-center w-5 shrink-0">
                    <Info class="h-4.5 w-4.5" />
                  </div>
                  {t(msg`Notes`)}
                </div>
                <kbd
                  class="h-4.5 min-w-[15px] inline-flex items-center justify-center rounded border border-primary/20 bg-primary/5 px-1.5 font-mono text-[9px] font-bold text-primary opacity-0 group-hover/notes:opacity-100 transition-opacity select-none cursor-help"
                  title={t(msg`Add notes: Press N to focus the notes field`)}
                >
                  {t(msg`N`)}
                </kbd>
              </div>
              <div class="px-3 pb-1.5">
                <textarea
                  class="todo-notes-input min-h-[32px] w-full bg-transparent p-0 text-sm focus-visible:outline-none resize-none overflow-hidden [field-sizing:content]"
                  placeholder={t(msg`Add a note...`)}
                  value={data().note ?? ""}
                  onInput={(e) => {
                    e.currentTarget.style.height = "auto";
                    e.currentTarget.style.height = `${e.currentTarget.scrollHeight}px`;
                    debouncedHandleUpdate({ note: e.currentTarget.value });
                  }}
                  ref={(el) => {
                    notesRef = el;
                    requestAnimationFrame(() => {
                      const sh = el.scrollHeight;
                      if (sh > 0) {
                        el.style.height = "auto";
                        el.style.height = `${sh}px`;
                      } else {
                        el.style.height = "auto";
                      }
                    });
                  }}
                />
              </div>
            </GlassSurface>

            {/* Delete Action */}
            <GlassSurface class="flex flex-col p-1.5 mt-4">
              <DetailActionButton
                label={t(msg`Delete Task`)}
                icon={Trash2}
                isDestructive
                onClick={() => setIsDeleteDialogOpen(true)}
                shortcut={t(msg`Del`)}
                shortcutTitle={t(msg`Delete task: Press Del to delete this task`)}
              />
            </GlassSurface>

            <ConfirmDialog
              open={isDeleteDialogOpen()}
              onOpenChange={setIsDeleteDialogOpen}
              title={t(msg`Delete Task`)}
              description={t(
                "Are you sure you want to delete this task? This action cannot be undone.",
              )}
              variant="destructive"
              confirmLabel={t(msg`Delete`)}
              onConfirm={() => {
                const dbInstance = drizzle();
                if (dbInstance) {
                  void deleteTodoMutation(dbInstance, props.todoId);
                  props.onDelete?.();
                }
              }}
            />

            <div class="mt-auto pt-6 text-[10px] text-muted-foreground opacity-50 text-center">
              {t(msg`Created on`)}{" "}
              {data().created_at
                ? new Date(data().created_at ?? "").toLocaleString()
                : t(msg`Unknown`)}
            </div>
          </div>
        </ListThemeProvider>
      </Show>
    </div>
  );
}
